/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The date-range picker card. A field-selector chip picks WHICH date column the
 * range targets; a preset chip (全部/本月/上月/近7天/自定义) sets the range; and the
 * start/end fields each open a date picker. Picking either bound makes the range
 * custom (the explicit pick wins over the active preset). All range state is
 * owned upstream — this card is fully controlled.
 *
 * Clearing works at both levels: a custom bound carries its own ✕ (leaving the
 * other bound in force as a half-open range), and the preset chip carries one
 * that drops the whole range back to 全部.
 */

import { Typography } from '@euler/app/components/typography';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import {
  LeadDateField,
  LeadDatePresetKey,
  kLeadDateFields,
  kLeadDatePresets,
} from '@euler/app/flows/leads/pages/list/functions/leadListConstants';
import {
  DateTimePicker,
  useDateTimePicker,
} from '@euler/components/DateTimePicker';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { CalendarDays, ChevronDown, X } from 'lucide-react-native';
import { memo, useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';

/**
 * One bound field (start/end): a calendar glyph + its resolved label, plus a ✕
 * when `onClear` is given (i.e. this bound holds an explicit custom pick — a
 * preset's bounds are cleared through the preset chip instead).
 */
const DateBoundField = memo(
  ({
    label,
    onPress,
    onClear,
  }: {
    label: string;
    onPress: () => void;
    onClear?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={onPress}
        style={{
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          height: rpx(28),
          paddingHorizontal: rpx(10),
          borderRadius: rpx(10),
          backgroundColor: '#F8FAFC',
        }}
      >
        <CalendarDays size={rpx(14)} color={LD.brand} />
        <Typography.Text
          size={rpx(13)}
          color={LD.ink}
          medium
          numberOfLines={1}
          style={{ flex: 1, marginLeft: rpx(6) }}
        >
          {label}
        </Typography.Text>
        {onClear ? (
          <TouchableOpacity
            activeOpacity={0.7}
            onPress={onClear}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <X size={rpx(14)} color={LD.mist} />
          </TouchableOpacity>
        ) : null}
      </TouchableOpacity>
    );
  },
);

export const LeadDateRangePicker = memo(
  ({
    dateFieldLabel,
    onSelectDateField,
    presetKey,
    onSelectPreset,
    rangeStart,
    rangeEnd,
    customStart,
    customEnd,
    onCustomStart,
    onCustomEnd,
    onClearStart,
    onClearEnd,
    onClearRange,
  }: {
    dateFieldLabel: string;
    onSelectDateField: (key: LeadDateField) => void;
    presetKey: LeadDatePresetKey;
    onSelectPreset: (key: LeadDatePresetKey) => void;
    rangeStart: string;
    rangeEnd: string;
    customStart: Date | undefined;
    customEnd: Date | undefined;
    onCustomStart: (date: Date) => void;
    onCustomEnd: (date: Date) => void;
    onClearStart: () => void;
    onClearEnd: () => void;
    onClearRange: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { showActionSheetWithOptions } = useActionSheet();
    const [startShown, showStart, hideStart] = useDateTimePicker();
    const [endShown, showEnd, hideEnd] = useDateTimePicker();

    const presetLabel =
      kLeadDatePresets.find(p => p.key === presetKey)?.label ?? '全部';

    // The field chip picks WHICH date column the range filters (vs. the preset,
    // which picks the range itself).
    const openFieldSheet = useCallback(() => {
      const options = [...kLeadDateFields.map(f => f.label), '取消'];
      showActionSheetWithOptions(
        {
          options,
          cancelButtonIndex: kLeadDateFields.length,
          title: '按日期筛选',
        },
        selected => {
          if (selected == null || selected >= kLeadDateFields.length) return;
          onSelectDateField(kLeadDateFields[selected].key);
        },
      );
    }, [onSelectDateField, showActionSheetWithOptions]);

    const openPresetSheet = useCallback(() => {
      const options = [...kLeadDatePresets.map(p => p.label), '取消'];
      showActionSheetWithOptions(
        {
          options,
          cancelButtonIndex: kLeadDatePresets.length,
          title: '日期范围',
        },
        selected => {
          if (selected == null || selected >= kLeadDatePresets.length) return;
          const key = kLeadDatePresets[selected].key;
          onSelectPreset(key);
          // 自定义 needs explicit bounds — start with the from-date picker.
          if (key === 'custom') showStart();
        },
      );
    }, [onSelectPreset, showActionSheetWithOptions, showStart]);

    const onConfirmStart = useCallback(
      (date: Date) => {
        onCustomStart(date);
        hideStart();
      },
      [hideStart, onCustomStart],
    );

    const onConfirmEnd = useCallback(
      (date: Date) => {
        onCustomEnd(date);
        hideEnd();
      },
      [hideEnd, onCustomEnd],
    );

    return (
      <>
        <View
          style={{
            marginHorizontal: rpx(16),
            marginBottom: rpx(14),
            paddingVertical: rpx(9),
            paddingHorizontal: rpx(12),
            borderRadius: rpx(16),
            backgroundColor: LD.surface,
            borderWidth: 1,
            borderColor: LD.hairline,
            gap: rpx(8),
          }}
        >
          {/* Top row: which date column | which preset range. */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <TouchableOpacity
              activeOpacity={0.7}
              onPress={openFieldSheet}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingHorizontal: rpx(8),
                paddingVertical: rpx(4),
                borderRadius: rpx(999),
                backgroundColor: '#EEF5FF',
              }}
            >
              <Typography.Text size={rpx(12)} color={LD.brand} bold>
                {dateFieldLabel}
              </Typography.Text>
              <ChevronDown
                size={rpx(14)}
                color={LD.brand}
                style={{ marginLeft: rpx(2) }}
              />
            </TouchableOpacity>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: rpx(6),
              }}
            >
              <TouchableOpacity
                activeOpacity={0.7}
                onPress={openPresetSheet}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingHorizontal: rpx(10),
                  paddingVertical: rpx(5),
                  borderRadius: rpx(999),
                  backgroundColor: '#F1F5F9',
                }}
              >
                <Typography.Text size={rpx(12)} color={LD.steel} bold>
                  {presetLabel}
                </Typography.Text>
                <ChevronDown
                  size={rpx(14)}
                  color={LD.steel}
                  style={{ marginLeft: rpx(2) }}
                />
              </TouchableOpacity>
              {/* Whole-range reset — only meaningful once a range is in force. */}
              {presetKey !== 'all' ? (
                <TouchableOpacity
                  activeOpacity={0.7}
                  onPress={onClearRange}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <X size={rpx(15)} color={LD.mist} />
                </TouchableOpacity>
              ) : null}
            </View>
          </View>

          {/* Input row: start bound · 至 · end bound. */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: rpx(8),
            }}
          >
            <DateBoundField
              label={`开始 ${rangeStart}`}
              onPress={showStart}
              onClear={customStart ? onClearStart : undefined}
            />
            <Typography.Text size={rpx(13)} color={LD.steel} medium>
              至
            </Typography.Text>
            <DateBoundField
              label={`结束 ${rangeEnd}`}
              onPress={showEnd}
              onClear={customEnd ? onClearEnd : undefined}
            />
          </View>
        </View>

        {/* The modal pickers live OUTSIDE the card's flex layout — rendered as
            children they would add height/gap to the card while open, drifting
            the panel. As overlays their tree position doesn't matter. */}
        <DateTimePicker
          isVisible={startShown}
          mode="date"
          date={customStart}
          onConfirm={onConfirmStart}
          onCancel={hideStart}
          title="开始日期"
          confirmTextIOS="确认"
          cancelTextIOS="关闭"
        />
        <DateTimePicker
          isVisible={endShown}
          mode="date"
          date={customEnd ?? customStart}
          onConfirm={onConfirmEnd}
          onCancel={hideEnd}
          title="结束日期"
          confirmTextIOS="确认"
          cancelTextIOS="关闭"
        />
      </>
    );
  },
);
